import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader } from "@/components/layout/Layout";
import { Building2, Mail, CheckCircle2 } from "lucide-react";

const ROLE_LABELS = {
  admin_societe: "Admin société",
  responsable_etablissement: "Responsable établissement",
  consultation: "Consultation (lecture seule)",
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/organisations/invitations/${token}/info`);
        setInfo(data);
      } catch (err) {
        setError(fmtError(err));
      }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/organisations/invitations/${token}/accept`, {});
      toast.success("Invitation acceptée. Bienvenue !");
      nav("/app/mon-organisation");
    } catch (err) {
      toast.error(fmtError(err));
    } finally { setBusy(false); }
  };

  const emailMatches = user && info && (user.email || "").toLowerCase() === info.email.toLowerCase();

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <div className="max-w-lg mx-auto px-4 py-10" data-testid="accept-invitation-page">
        <h1 className="text-2xl font-semibold text-[#1F2933] inline-flex items-center gap-2">
          <Mail size={22} className="text-[#1B3A5C]" /> Invitation
        </h1>
        {error && <div className="mt-4 card p-4 text-sm text-[#C0392B]" data-testid="invite-error">{error}</div>}
        {info && !error && (
          <div className="mt-4 card p-4 space-y-3">
            <div className="text-sm text-[#3E4C59]">Vous êtes invité(e) à rejoindre</div>
            <div className="font-semibold text-[#1F2933] text-lg inline-flex items-center gap-2">
              <Building2 size={18} /> {info.organisation_nom}
            </div>
            <div className="text-sm">
              Établissement : <strong>{info.etablissement_nom}</strong><br />
              Rôle : <strong>{ROLE_LABELS[info.role] || info.role}</strong>
              {info.seuil_engagement_ht != null && <> — seuil {info.seuil_engagement_ht} € HT</>}
            </div>
            <div className="text-xs text-[#3E4C59]">
              Adresse invitée : <span className="font-mono">{info.email}</span>
            </div>
            <div className="pt-3 border-t border-[#E4E7EB]">
              {!user ? (
                <>
                  <p className="text-sm text-[#3E4C59] mb-2">
                    Connectez-vous avec <span className="font-mono">{info.email}</span> pour accepter,
                    ou <Link to="/inscription" className="text-[#1B3A5C] underline">créez un compte</Link> avec cette adresse.
                  </p>
                  <Link to={`/connexion?next=/invitation/${token}`} className="btn-primary" data-testid="invite-login-btn">
                    Se connecter
                  </Link>
                </>
              ) : !emailMatches ? (
                <div className="text-sm text-[#C0392B]">
                  Vous êtes connecté(e) sous {user.email} — cette invitation est adressée à {info.email}.
                  Déconnectez-vous puis reconnectez-vous avec la bonne adresse.
                </div>
              ) : (
                <button onClick={accept} disabled={busy} className="btn-primary" data-testid="invite-accept-btn">
                  <CheckCircle2 size={16} /> {busy ? "…" : "Accepter l'invitation"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
